import { useEffect, useMemo, useState } from "react";
import { _ROUTES } from "../../../utils/_routes_constants";
import { DataTable } from "../../ui/DataTable";
import { useBudgetData } from "../../../hooks/useBudgetData";
import { generateMonthColumns } from "../../../utils/AgGridUtils/ColumnBuilders/generateMonthColumns";
import {
  formDataToRow,
  MONTH_MAPPING_KEY,
  FORECAST_MONTH_MAPPING_KEY,
} from "./formDataToRow";
import { getBudgetVsLyValue } from "../../../utils/AgGridUtils/getBudgetVsLyValue";
import { currencyFormatter } from "../../../utils/AgGridUtils/ColumnBuilders/createColumn";

export const Table = ({
  rows,
  setRows,
  params,
  isDataLoading,
  onDataLoadingStart,
  onDataLoadingStop,
}) => {
  const [loadedData, setLoadedData] = useState([]);
  const { loadData } = useBudgetData({
    setRows,
    onDataLoadingStart,
    onDataLoadingStop,
    params,
    formDataToRow: (data) => formDataToRow(data),
  });

  useEffect(() => {
    const fetchData = async () => {
      const data = await loadData(_ROUTES.INDICATOR_TEMPLATE_VIEW, params);
      setLoadedData(data);
    };

    fetchData();
  }, [params]);

  // Generate columns based on fiscal years
  const generateFiscalYearColumns = useMemo(() => {
    if (!loadedData || !loadedData.length === 0) {
      return [];
    }

    // Create a map to store unique fiscal year and type combinations
    const fiscalYearTypeMap = new Map();

    // Process the data to extract fiscal year and type information
    loadedData.forEach((item) => {
      const key = `${item.fiscal_year}|${item.fiscal_year_type}`;
      fiscalYearTypeMap.set(key, {
        year: item.fiscal_year,
        type: item.fiscal_year_type,
        version: item.fiscal_year_version,
      });
    });

    // Convert to array and sort by year
    const fiscalYearsArray = Array.from(fiscalYearTypeMap.entries())
      .map(([key, value]) => ({
        key,
        ...value,
      }))
      .sort((a, b) => {
        const yearA = parseInt(a.year.split("-")[0]);
        const yearB = parseInt(b.year.split("-")[0]);
        return yearA - yearB;
      });

    // Generate columns from the sorted array
    const columns = fiscalYearsArray.map((item, index) => {
      const { key, year, version, type } = item;

      return {
        headerName:
          type === "B"
            ? `Budget ${year} - ${version}`
            : type === "F"
            ? `Forecast ${year} - ${version}`
            : year,
        field: `${MONTH_MAPPING_KEY}.${key}`,
        headerStyle: (params) => {
          const style = {
            color: "#000",
          };

          if (type === "B") {
            style.backgroundColor = "rgb(254, 248, 160)";
          } else if (type === "F") {
            style.backgroundColor = "rgb(145, 228, 251)";
          } else {
            style.backgroundColor = "rgb(174, 221, 148)";
          }

          return style;
        },
        // Open by default the last 2 fiscal years
        openByDefault: index >= fiscalYearsArray.length - 2,
        suppressHeaderMenuButton: true,
        children: [
          ...generateMonthColumns([], {
            cellRenderer: (params) => {
              if (params.data?.is_ton) {
                return `${params.value} T`;
              } else if (params.data?.is_percent) {
                return `${params.value}%`;
              }

              return params.value;
            },
            cellStyle: (params) => {
              // Skip styling if params or data is missing
              if (!params || !params.data) {
                return { backgroundColor: "#ADD8E6" }; // Light blue
              }

              // Extract the month number from the column ID
              const columnId = params.column.getColId();
              const monthMatch = columnId.match(/\.(\d+)$/);

              if (!monthMatch) {
                return null;
              }

              const month = parseInt(monthMatch[1], 10);

              // Check if this specific month is marked as a forecast in the data
              const forecastMonths =
                params.data[FORECAST_MONTH_MAPPING_KEY]?.[key];

              if (forecastMonths && forecastMonths[month]) {
                // Highlight forecast data in red
                return { color: "#FF0000", fontStyle: "italic" };
              }

              return null;
            },
            editable: false,
            _custom: {
              monthKeyPrefix: `${MONTH_MAPPING_KEY}.${key}`,
            },
          }),
          {
            headerName: "Total",
            headerClass: "total-header-col",
            field:
              type === "B"
                ? "budget_total"
                : type === "F"
                ? "forecast_total"
                : "real_total",
            editable: false,
            cellDataType: "number",
            suppressHeaderMenuButton: true,
            valueFormatter: (params) => {
              if (!params || !params.data || !params.data[MONTH_MAPPING_KEY]) {
                return "";
              }

              const fiscalYearData = params.data[MONTH_MAPPING_KEY][key];

              if (!fiscalYearData) {
                return "";
              }

              const total = Object.values(fiscalYearData).reduce(
                (acc, curr) => {
                  return acc + curr;
                },
                0,
              );

              return params.data?.is_ton
                ? `${total} T`
                : params.data?.is_percent
                ? `${total}%`
                : total;
            },
          },
        ],
      };
    });

    return columns;
  }, [loadedData]);

  const handleAutoSizeColumns = (event) => {
    event.api.autoSizeAllColumns();
    event.api.sizeColumnsToFit();
  };
  

  return (
    <DataTable
      isDataLoading={isDataLoading}
      rows={rows}
      columnsIncludeMonths={false}
      columns={[
        {
          headerName: "",
          field: "activity",
          flex: 2,
          hide: true,
          rowGroup: true,
          suppressHeaderMenuButton: true,
        },
        {
          headerName: "",
          field: "site",
          flex: 2,
          hide: true,
          rowGroup: true,
          suppressHeaderMenuButton: true,
        },
        {
          headerName: "",
          field: "name",
          flex: 2,
          showRowGroup: "activity",
          suppressHeaderMenuButton: true,
          // For rows
          wrapText: true,
          autoHeight: true,
          // For header
          wrapHeaderText: true,
          autoHeaderHeight: true,
          pinned: "left",
        },
        ...generateFiscalYearColumns,
        ...(rows && rows.length > 0
          ? [
              {
                headerName: "Budget vs Last Year",
                field: "budget_vs_ly",
                editable: false,
                openByDefault: true,
                suppressHeaderMenuButton: true,
                headerStyle: {
                  color: "#000",
                  backgroundColor: "rgb(246, 162, 191)",
                },
                children: [
                  {
                    headerName: "Rs",
                    field: "budget_vs_ly_rs",
                    editable: false,
                    cellDataType: "number",
                    suppressHeaderMenuButton: true,
                    valueFormatter: (params) => {
                      return currencyFormatter(getBudgetVsLyValue(params));
                    },
                  },
                  {
                    headerName: "%",
                    field: "budget_vs_ly_percent",
                    editable: false,
                    cellDataType: "number",
                    suppressHeaderMenuButton: true,
                    valueFormatter: (params) => {
                      return currencyFormatter(getBudgetVsLyValue(params, true));
                    },
                  },
                ],
              },
            ]
          : []),
      ]}
      agGridProps={{
        defaultColDef: {
          suppressSizeToFit: true,
        },
        autoSizeStrategy: {
          type: "fitCellContents",
        },
        onGridReady: handleAutoSizeColumns,
        onColumnGroupOpened: handleAutoSizeColumns,
        onGridSizeChanged: handleAutoSizeColumns,
        groupDisplayType: "groupRows",
        getRowStyle: (params) => {
          const style = {};
          if (params.node?.data?.color_background) {
            style.background = params.node.data.color_background;
          }

          if (params.node?.data?.color_text) {
            style.color = params.node.data.color_text;
          }

          if (params.node?.data?.children?.display_children === true) {
            style.color = "grey";
          } 

          return style;
        },
      }}
    />
  );
};
